import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { format } from 'date-fns';

import { DepartmentsBudgetsReportTableProvider } from './context/DepartmentsBudgetsReportTableContext';
import DepartmentsBudgetsReportTable from './DepartmentsBudgetsReportTable';

import { getDateFnsLocale } from 'Helpers/localeService';
import { apiClient } from 'Helpers/network';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

const DepartmentsBudgetsReview = ({ reviewDataUrl, resendUrl, archiveUrl, editMode: initialEditMode }) => {
    const [editMode, setEditMode] = useState(initialEditMode);
    const [editedComments, setEditedComments] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toast = useToast();

    const budgetsDataRequest = useQuery({
        queryFn: () => apiClient.get(reviewDataUrl),
    });

    const handleCommentChange = useCallback((categoryId, value) => {
        setEditedComments((prev) => ({
            ...prev,
            [categoryId]: value,
        }));
    }, []);

    const handleResend = useCallback(async () => {
        if (!resendUrl) return;

        setIsSubmitting(true);
        try {
            await apiClient.post(resendUrl, { comments: editedComments });
            toast.success($t('Review re-sent successfully.'));
            // Redirect back to archive
            if (archiveUrl) {
                window.location.href = archiveUrl;
            }
        } catch (error) {
            console.error('Failed to resend review:', error);
            toast.error($t('Failed to re-send review. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    }, [resendUrl, editedComments, archiveUrl, toast]);

    const handleCancelEdit = useCallback(() => {
        setEditMode(false);
        setEditedComments(budgetsDataRequest.data?.flat_comments || {});
    }, [budgetsDataRequest.data?.flat_comments]);

    // All comments for display (array of all comments per category, with author info)
    const allComments = useMemo(() => {
        return budgetsDataRequest.data?.comments || {};
    }, [budgetsDataRequest.data?.comments]);

    // Get status label for display
    const getStatusLabel = () => {
        const status = budgetsDataRequest.data?.status;
        const hasBeenReviewed = budgetsDataRequest.data?.has_been_reviewed;
        if (status === 'reviewing') {
            return $t('Under Review');
        }
        if (hasBeenReviewed) {
            return $t('Reviewed');
        }
        return $t('Awaiting Review');
    };

    return (
        <div className="tw-h-full tw-flex tw-flex-col tw-space-y-6 tw-py-12">
            {editMode && (
                <Alert type="info">
                    {$t('You are in edit mode. Modify the comments below and click "Re-Send" to send the updated review back to the reviewer.')}
                </Alert>
            )}

            {!budgetsDataRequest.loading && !budgetsDataRequest.data?.has_been_reviewed && (
                <Alert type="warning">{$t('This budget is not yet reviewed.')}</Alert>
            )}

            <DepartmentsBudgetsReportTableProvider
                criteria={budgetsDataRequest.data?.criteria}
                report={budgetsDataRequest.data?.report}
                budgets={budgetsDataRequest.data?.budgets}
                allComments={allComments}
                userType={budgetsDataRequest.data?.user_type || 'creator'}
                loading={budgetsDataRequest.loading}
                showComments
                editableComments={editMode}
                editedComments={editedComments}
                onCommentChange={handleCommentChange}
            >
                <DepartmentsBudgetsReportTable budgetsData={budgetsDataRequest.data?.budgets_data || []} />
            </DepartmentsBudgetsReportTableProvider>

            <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-start md:tw-justify-between">
                <table className="tw-max-w-md">
                    <tbody>
                        <tr>
                            <td className="tw-font-medium tw-pr-4">{$t('Status')}:</td>
                            <td>{getStatusLabel()}</td>
                        </tr>
                        <tr>
                            <td className="tw-font-medium tw-pr-4">{$t('Reviewer Email')}:</td>
                            <td>{budgetsDataRequest.data?.reviewer_email || '-'}</td>
                        </tr>
                        <tr>
                            <td className="tw-font-medium tw-pr-4">{$t('Reviewer Name')}:</td>
                            <td>{budgetsDataRequest.data?.reviewer_name || '-'}</td>
                        </tr>
                        {budgetsDataRequest.data?.reviewed_at && (
                            <tr>
                                <td className="tw-font-medium tw-pr-4">{$t('Reviewed At')}:</td>
                                <td>{format(new Date(budgetsDataRequest.data.reviewed_at), 'PPpp', { locale: getDateFnsLocale() })}</td>
                            </tr>
                        )}
                        {budgetsDataRequest.data?.requested_at && (
                            <tr>
                                <td className="tw-font-medium tw-pr-4">{$t('Requested At')}:</td>
                                <td>{format(new Date(budgetsDataRequest.data.requested_at), 'PPpp', { locale: getDateFnsLocale() })}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {editMode && resendUrl && (
                    <div className="tw-flex tw-gap-3">
                        <Button type="button" variant="secondary" onClick={handleCancelEdit} disabled={isSubmitting}>
                            {$t('Cancel')}
                        </Button>
                        <Button type="button" variant="primary" onClick={handleResend} loading={isSubmitting} disabled={isSubmitting}>
                            {$t('Re-Send')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

if (document.getElementById('DepartmentsBudgetsReview')) {
    var rootEl = document.getElementById('DepartmentsBudgetsReview');
    const reviewDataUrl = rootEl.getAttribute('data-review-data-url');
    const resendUrl = rootEl.getAttribute('data-resend-url');
    const archiveUrl = rootEl.getAttribute('data-archive-url');
    const editMode = rootEl.getAttribute('data-edit-mode') === 'true';

    ReactDOM.render(
        <App>
            <DepartmentsBudgetsReview reviewDataUrl={reviewDataUrl} resendUrl={resendUrl} archiveUrl={archiveUrl} editMode={editMode} />
        </App>,
        rootEl,
    );
}
